import { useState, useEffect, useCallback, useRef } from "react";
import { GoogleMap, useJsApiLoader, Marker, Polyline, InfoWindow } from "@react-google-maps/api";
import { API , authFetch } from "@/App";
import { MapPin, Navigation, Battery, Clock, RefreshCw, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const mapContainerStyle = { width: "100%", height: "500px", borderRadius: "12px" };
const defaultCenter = { lat: 23.0, lng: 72.6 }; // Gujarat center

const ROLE_COLORS = {
  AREA_MANAGER: "#8B1A1A",
  SALES_EXECUTIVE: "#3B82F6",
  PROMOTOR: "#10B981",
};

const LiveTrackingMap = () => {
  const [positions, setPositions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [mapError, setMapError] = useState(false);
  const intervalRef = useRef(null);

  // Detect Google Maps auth failure
  useEffect(() => {
    window.gm_authFailure = () => setMapError(true);
    return () => { window.gm_authFailure = undefined; };
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
  });

  const fetchLivePositions = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/gps/live`, {  });
      const data = await res.json();
      if (data.success) {
        setPositions(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch live positions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTrail = useCallback(async (employeeId) => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await authFetch(`${API}/gps/trail/${employeeId}?date=${today}`, {  });
      const data = await res.json();
      if (data.success) {
        setTrail({ employeeId, ...data.data });
      }
    } catch (err) {
      console.error("Failed to fetch trail:", err);
    }
  }, []);

  useEffect(() => {
    fetchLivePositions();
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLivePositions, 30000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchLivePositions, autoRefresh]);

  const handleMarkerClick = (emp) => {
    setSelectedEmployee(emp);
    fetchTrail(emp.employee_id);
  };

  if (!isLoaded || loadError || mapError) {
    return (
      <div className="space-y-4" data-testid="live-tracking-map">
        {/* Controls Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-slate-900 font-['Sora']">Live Employee Tracking</h3>
            <Badge variant="outline" className="text-xs" data-testid="tracking-count">
              <MapPin className="w-3 h-3 mr-1" />
              {positions.length} Active
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchLivePositions}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
              data-testid="refresh-map-btn"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
        </div>

        {/* Map Error Fallback */}
        {(loadError || mapError) && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Google Maps requires domain whitelisting</p>
              <p className="text-xs text-amber-600 mt-1">
                Add <code className="bg-amber-100 px-1 rounded">*.preview.emergentagent.com/*</code> to your Google Maps API key's HTTP referrer restrictions in the Google Cloud Console.
              </p>
            </div>
          </div>
        )}

        {/* Fallback: Employee Position Table */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="employee-position-cards">
          {positions.map((emp) => (
            <Card key={emp.employee_id} className="p-3" data-testid={`employee-card-${emp.employee_id}`}>
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                  style={{ backgroundColor: ROLE_COLORS[emp.role] || "#6B7280" }}
                >
                  {emp.full_name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{emp.full_name}</p>
                  <p className="text-xs text-slate-500">{emp.role.replace(/_/g, " ")}</p>
                </div>
                {emp.is_stoppage ? (
                  <Badge variant="outline" className="text-yellow-700 border-yellow-300 bg-yellow-50 text-xs shrink-0">Stop</Badge>
                ) : (
                  <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 text-xs shrink-0">Moving</Badge>
                )}
              </div>
              <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{emp.lat?.toFixed(4)}, {emp.lng?.toFixed(4)}</span>
                <span className="flex items-center gap-1"><Navigation className="w-3 h-3" />{emp.speed || 0} km/h</span>
                {emp.battery_level && <span className="flex items-center gap-1"><Battery className="w-3 h-3" />{emp.battery_level}%</span>}
              </div>
            </Card>
          ))}
          {positions.length === 0 && !loading && (
            <div className="col-span-full text-center py-8 text-slate-500">
              <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No active employee positions. Click "Simulate GPS Data" above.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const mapCenter = positions.length > 0
    ? { lat: positions[0].lat, lng: positions[0].lng }
    : defaultCenter;

  return (
    <div className="space-y-4" data-testid="live-tracking-map">
      {/* Controls Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-slate-900 font-['Sora']">Live Employee Tracking</h3>
          <Badge variant="outline" className="text-xs" data-testid="tracking-count">
            <MapPin className="w-3 h-3 mr-1" />
            {positions.length} Active
          </Badge>
        </div>
        <div className="flex items-center gap-3">
          {/* Legend */}
          <div className="hidden md:flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#8B1A1A]" />Manager</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#3B82F6]" />Sales Exec</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#10B981]" />Promotor</span>
          </div>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              autoRefresh ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
            }`}
            data-testid="auto-refresh-toggle"
          >
            <RefreshCw className={`w-3 h-3 ${autoRefresh ? "animate-spin" : ""}`} />
            {autoRefresh ? "Live" : "Paused"}
          </button>
          <button
            onClick={fetchLivePositions}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
            data-testid="refresh-map-btn"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={9}
          onLoad={() => setMapError(false)}
          onError={() => setMapError(true)}
          options={{
            styles: [
              { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
            ],
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
          }}
        >
          {positions.map((emp) => (
            <Marker
              key={emp.employee_id}
              position={{ lat: emp.lat, lng: emp.lng }}
              onClick={() => handleMarkerClick(emp)}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                scale: 10,
                fillColor: ROLE_COLORS[emp.role] || "#6B7280",
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
              }}
              title={emp.full_name}
            />
          ))}

          {/* Draw trail polyline for selected employee */}
          {trail && trail.trail && trail.trail.length > 1 && (
            <Polyline
              path={trail.trail.map((p) => ({ lat: p.lat, lng: p.lng }))}
              options={{
                strokeColor: "#8B1A1A",
                strokeOpacity: 0.8,
                strokeWeight: 3,
              }}
            />
          )}

          {/* Stoppage markers */}
          {trail && trail.stoppages && trail.stoppages.map((stop, idx) => (
            <Marker
              key={`stop-${idx}`}
              position={{ lat: stop.lat, lng: stop.lng }}
              icon={{
                path: window.google?.maps?.SymbolPath?.BACKWARD_CLOSED_ARROW || 3,
                scale: 6,
                fillColor: "#EAB308",
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
              }}
              title={`Stoppage: ${stop.stoppage_duration_minutes || 0} min`}
            />
          ))}

          {/* Info Window for selected employee */}
          {selectedEmployee && (
            <InfoWindow
              position={{ lat: selectedEmployee.lat, lng: selectedEmployee.lng }}
              onCloseClick={() => { setSelectedEmployee(null); setTrail(null); }}
            >
              <div className="p-1 min-w-[180px]">
                <p className="font-semibold text-sm text-slate-900">{selectedEmployee.full_name}</p>
                <p className="text-xs text-slate-500">{selectedEmployee.employee_code} &middot; {selectedEmployee.role.replace("_", " ")}</p>
                <div className="mt-2 space-y-1 text-xs text-slate-600">
                  <p className="flex items-center gap-1">
                    <Navigation className="w-3 h-3" />
                    Speed: {selectedEmployee.speed || 0} km/h
                  </p>
                  {selectedEmployee.battery_level && (
                    <p className="flex items-center gap-1">
                      <Battery className="w-3 h-3" />
                      Battery: {selectedEmployee.battery_level}%
                    </p>
                  )}
                  <p className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Updated: {new Date(selectedEmployee.last_updated).toLocaleTimeString("en-IN")}
                  </p>
                  {selectedEmployee.is_stoppage && (
                    <Badge variant="outline" className="text-yellow-700 border-yellow-300 bg-yellow-50 text-xs mt-1">
                      At Stoppage
                    </Badge>
                  )}
                </div>
                {trail && (
                  <p className="text-xs text-slate-400 mt-2 border-t pt-1">
                    {trail.total_pings} GPS pings today &middot; {trail.stoppages?.length || 0} stoppages
                  </p>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>

      {/* Employee Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="employee-position-cards">
        {positions.map((emp) => (
          <Card
            key={emp.employee_id}
            className={`p-3 cursor-pointer transition-all hover:shadow-md ${
              selectedEmployee?.employee_id === emp.employee_id ? "ring-2 ring-[#8B1A1A]" : ""
            }`}
            onClick={() => handleMarkerClick(emp)}
            data-testid={`employee-card-${emp.employee_id}`}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                style={{ backgroundColor: ROLE_COLORS[emp.role] || "#6B7280" }}
              >
                {emp.full_name?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{emp.full_name}</p>
                <p className="text-xs text-slate-500">{emp.role.replace("_", " ")}</p>
              </div>
              {emp.is_stoppage ? (
                <Badge variant="outline" className="text-yellow-700 border-yellow-300 bg-yellow-50 text-xs shrink-0">Stop</Badge>
              ) : (
                <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 text-xs shrink-0">Moving</Badge>
              )}
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Navigation className="w-3 h-3" />{emp.speed || 0} km/h</span>
              {emp.battery_level && <span className="flex items-center gap-1"><Battery className="w-3 h-3" />{emp.battery_level}%</span>}
            </div>
          </Card>
        ))}
        {positions.length === 0 && !loading && (
          <div className="col-span-full text-center py-8 text-slate-500">
            <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No active employee positions. Run GPS simulation first.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveTrackingMap;
